import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  MapPin, Calendar, Navigation, Building2, Phone, Star,
  CheckCircle, ExternalLink, Route, Clock, ChevronRight,
  Presentation, Users, TrendingUp, LogOut, CreditCard, Bell, BarChart3, Briefcase
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { getVersionString } from '../config/version';
import UserNotificationCenter from '../components/UserNotificationCenter';
import BusinessStatusModal from '../components/BusinessStatusModal';
import SalesNotificationSystem from '../components/SalesNotificationSystem';
import { LOGO_DARK_BG } from '../config/branding';

const VertriebDashboardPage = () => {
  const navigate = useNavigate();
  const [tours, setTours] = useState([]);
  const [activeTour, setActiveTour] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total_leads: 0, converted: 0, pending: 0 });
  const [salesperson, setSalesperson] = useState(null);
  
  // Status Modal State
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [showStatusModal, setShowStatusModal] = useState(false);

  // Check authentication
  const token = localStorage.getItem('salesperson_token');
  const salespersonId = localStorage.getItem('salesperson_id');
  const salespersonName = localStorage.getItem('salesperson_name');

  useEffect(() => {
    // Redirect to login if not authenticated
    if (!token || !salespersonId) {
      navigate('/vertrieb/login');
      return;
    }
    loadData();
  }, [token, salespersonId, navigate]);

  const loadData = async () => {
    try {
      // Load tours for this salesperson
      const toursRes = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/sales-tours/tours?salesperson_id=${salespersonId}`
      );
      const toursData = await toursRes.json();
      setTours(Array.isArray(toursData) ? toursData : []);

      // Find active tour (today's tour)
      const today = new Date().toISOString().split('T')[0];
      const todayTour = toursData.find(t => t.date === today && t.status !== 'abgeschlossen');
      setActiveTour(todayTour);

      // Load leads stats
      const leadsRes = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/vertrieb/leads?admin_token=${localStorage.getItem('admin_token') || ''}`
      );
      if (leadsRes.ok) {
        const leadsData = await leadsRes.json();
        const leads = Array.isArray(leadsData) ? leadsData : [];
        setStats({
          total_leads: leads.length,
          converted: leads.filter(l => l.status === 'converted').length,
          pending: leads.filter(l => l.status === 'follow_up').length
        });
      }
    } catch (error) {
      console.error('Load error:', error);
    } finally {
      setLoading(false);
    }
  };

  const markVisited = async (tourId, placeId) => {
    try {
      await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/sales-tours/tours/${tourId}/mark-visited/${placeId}`,
        { method: 'POST' }
      );
      loadData();
    } catch (error) {
      console.error('Mark visited error:', error);
    }
  };

  // Open status modal for a business
  const openStatusModal = (business) => {
    setSelectedBusiness(business);
    setShowStatusModal(true);
  };

  // Handle status update callback
  const handleStatusUpdated = (placeId, status, contact) => {
    // Also mark as visited in the tour
    if (activeTour) {
      markVisited(activeTour.id, placeId);
    }
    setShowStatusModal(false);
    setSelectedBusiness(null);
    loadData();
  };

  const openInMaps = (business) => {
    const url = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(business.address)}`;
    window.open(url, '_blank');
  };

  const handleLogout = () => {
    localStorage.removeItem('salesperson_token');
    localStorage.removeItem('salesperson_id');
    localStorage.removeItem('salesperson_name');
    navigate('/vertrieb/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-brand-green border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header - Mobile Optimized */}
      <header className="bg-slate-900 border-b border-slate-700 px-4 sm:px-6 py-3 sm:py-4 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto">
          {/* Top Row - Logo and Logout */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-3">
              <img src={LOGO_DARK_BG} alt="Dein Gutachten Direkt" className="h-8 sm:h-10" />
              <span className="text-xs bg-brand-green/20 text-brand-green px-2 py-1 rounded-full font-semibold hidden sm:inline">
                Vertrieb
              </span>
            </div>
            <div className="flex items-center gap-2 sm:gap-4">
              {/* Neues Echtzeit-Benachrichtigungssystem mit Sound */}
              <SalesNotificationSystem 
                salespersonId={salespersonId}
                token={token}
              />
              <button
                onClick={() => navigate('/vertrieb/profil')}
                className="text-xs sm:text-sm text-brand-green hover:text-green-400 font-medium truncate max-w-[100px] sm:max-w-none"
              >
                {salespersonName}
              </button>
              <Button variant="ghost" size="sm" onClick={handleLogout} className="text-gray-300 hover:text-red-400 px-2 sm:px-3">
                <LogOut className="w-4 h-4" />
                <span className="hidden sm:inline ml-1">Abmelden</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-6 md:py-8">
        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-6 md:mb-8">
          <div className="bg-white rounded-xl p-4 md:p-6 border border-gray-200">
            <div className="flex items-center gap-2 md:gap-3 mb-2">
              <div className="w-8 h-8 md:w-10 md:h-10 bg-brand-green/10 rounded-lg flex items-center justify-center">
                <Users className="w-4 h-4 md:w-5 md:h-5 text-brand-green" />
              </div>
              <span className="text-xs md:text-sm text-gray-600">Kontakte</span>
            </div>
            <div className="text-2xl md:text-3xl font-bold text-gray-900">{stats.total_leads}</div>
          </div>
          
          <div className="bg-white rounded-xl p-4 md:p-6 border border-gray-200">
            <div className="flex items-center gap-2 md:gap-3 mb-2">
              <div className="w-8 h-8 md:w-10 md:h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <CheckCircle className="w-4 h-4 md:w-5 md:h-5 text-green-600" />
              </div>
              <span className="text-xs md:text-sm text-gray-600">Konvertiert</span>
            </div>
            <div className="text-2xl md:text-3xl font-bold text-green-600">{stats.converted}</div>
          </div>
          
          <div className="bg-white rounded-xl p-4 md:p-6 border border-gray-200">
            <div className="flex items-center gap-2 md:gap-3 mb-2">
              <div className="w-8 h-8 md:w-10 md:h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                <Clock className="w-4 h-4 md:w-5 md:h-5 text-orange-600" />
              </div>
              <span className="text-xs md:text-sm text-gray-600">Follow-up</span>
            </div>
            <div className="text-2xl md:text-3xl font-bold text-orange-600">{stats.pending}</div>
          </div>
          
          <div className="bg-white rounded-xl p-4 md:p-6 border border-gray-200">
            <div className="flex items-center gap-2 md:gap-3 mb-2">
              <div className="w-8 h-8 md:w-10 md:h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Route className="w-4 h-4 md:w-5 md:h-5 text-blue-600" />
              </div>
              <span className="text-xs md:text-sm text-gray-600">Touren</span>
            </div>
            <div className="text-2xl md:text-3xl font-bold text-blue-600">
              {tours.filter(t => t.status === 'geplant').length}
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4 mb-6 md:mb-8">
          <Button
            onClick={() => navigate('/vertrieb/praesentation')}
            className="bg-brand-green hover:bg-brand-green-dark text-white p-4 h-auto flex flex-col items-center justify-center rounded-xl min-h-[100px]"
          >
            <Presentation className="w-7 h-7 mb-2" />
            <div className="text-center">
              <div className="text-sm font-bold">Präsentation</div>
              <div className="text-xs opacity-80">Partner-Akquise</div>
            </div>
          </Button>
          
          <Button
            onClick={() => navigate('/vertrieb/performance')}
            className="bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 text-white p-4 h-auto flex flex-col items-center justify-center rounded-xl min-h-[100px]"
            data-testid="performance-btn"
          >
            <BarChart3 className="w-7 h-7 mb-2" />
            <div className="text-center">
              <div className="text-sm font-bold">Performance</div>
              <div className="text-xs opacity-80">Meine Stats</div>
            </div>
          </Button>
          
          <Button
            onClick={() => navigate('/vertrieb/kontakte')}
            className="bg-gradient-to-r from-orange-500 to-amber-600 hover:from-orange-600 hover:to-amber-700 text-white p-4 h-auto flex flex-col items-center justify-center rounded-xl min-h-[100px]"
            data-testid="contacts-btn"
          >
            <Briefcase className="w-7 h-7 mb-2" />
            <div className="text-center">
              <div className="text-sm font-bold">Kontakte</div>
              <div className="text-xs opacity-80">CRM & Follow-ups</div>
            </div>
          </Button>
          
          <Button
            onClick={() => navigate('/vertrieb/visitenkarte')}
            variant="outline"
            className="p-4 h-auto flex flex-col items-center justify-center rounded-xl border-2 min-h-[100px]"
          >
            <CreditCard className="w-7 h-7 text-brand-green mb-2" />
            <div className="text-center">
              <div className="text-sm font-bold text-gray-900">Visitenkarte</div>
              <div className="text-xs text-gray-600">Digital teilen</div>
            </div>
          </Button>

          <Button
            onClick={() => navigate('/vertrieb/benachrichtigungen')}
            variant="outline"
            className="p-4 h-auto flex flex-col items-center justify-center rounded-xl border-2 min-h-[100px]"
            data-testid="notification-settings-btn"
          >
            <Bell className="w-7 h-7 text-brand-green mb-2" />
            <div className="text-center">
              <div className="text-sm font-bold text-gray-900">Benachricht.</div>
              <div className="text-xs text-gray-600">Einstellungen</div>
            </div>
          </Button>
          
          <Button
            onClick={() => window.open('tel:+4941018471160')}
            variant="outline"
            className="p-4 h-auto flex flex-col items-center justify-center rounded-xl border-2 min-h-[100px]"
          >
            <Phone className="w-7 h-7 text-brand-green mb-2" />
            <div className="text-center">
              <div className="text-sm font-bold text-gray-900">Zentrale</div>
              <div className="text-xs text-gray-600">04101 847 11 60</div>
            </div>
          </Button>
        </div>

        {/* Active Tour */}
        {activeTour && (
          <div className="bg-white rounded-2xl border-2 border-brand-green p-4 sm:p-6 mb-6 sm:mb-8">
            {/* Header - Mobile optimized */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4 sm:mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-brand-green rounded-xl flex items-center justify-center flex-shrink-0">
                  <Navigation className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-lg sm:text-xl font-bold text-gray-900">Heutige Tour</h2>
                  <p className="text-sm text-gray-600 line-clamp-1">{activeTour.location_formatted}</p>
                </div>
              </div>
              <div className="flex items-center justify-between sm:justify-end gap-4">
                <div className="text-left sm:text-right">
                  <div className="text-xs sm:text-sm text-gray-500">Betriebe</div>
                  <div className="text-xl sm:text-2xl font-bold text-brand-green">
                    {activeTour.visited_businesses?.length || 0} / {activeTour.business_count}
                  </div>
                </div>
              </div>
            </div>

            {/* Route starten Button - Prominent for mobile */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4 sm:mb-6">
              <Button
                onClick={() => {
                  // Start navigation to first unvisited business
                  const unvisited = activeTour.businesses?.find(b => 
                    !activeTour.visited_businesses?.some(v => v.place_id === b.place_id)
                  );
                  if (unvisited) {
                    const url = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(unvisited.address)}&travelmode=driving`;
                    window.open(url, '_blank');
                  }
                }}
                className="w-full bg-brand-green hover:bg-green-600 text-white py-3 text-base font-semibold"
              >
                <Route className="w-5 h-5 mr-2" />
                Route starten
              </Button>
              <Button
                onClick={() => {
                  // Open Google Maps with all waypoints
                  const businesses = activeTour.businesses?.slice(0, 10) || [];
                  const waypoints = businesses.map(b => encodeURIComponent(b.address)).join('|');
                  const origin = encodeURIComponent(activeTour.location_formatted);
                  const url = `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${origin}&waypoints=${waypoints}&travelmode=driving`;
                  window.open(url, '_blank');
                }}
                variant="outline"
                className="w-full border-blue-600 text-blue-600 hover:bg-blue-50 py-3"
              >
                <MapPin className="w-5 h-5 mr-2" />
                Ganze Tour ({activeTour.total_distance_km} km)
              </Button>
            </div>

            {/* Business List - Mobile optimized */}
            <div className="space-y-3 max-h-[60vh] sm:max-h-96 overflow-y-auto">
              {activeTour.businesses?.map((business, idx) => {
                const isVisited = activeTour.visited_businesses?.some(v => v.place_id === business.place_id);
                return (
                  <div 
                    key={business.place_id}
                    className={`p-3 sm:p-4 rounded-xl border-2 transition-all ${
                      isVisited 
                        ? 'bg-green-50 border-green-200' 
                        : 'bg-gray-50 border-gray-200'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${
                        isVisited ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-600'
                      }`}>
                        {isVisited ? <CheckCircle className="w-5 h-5" /> : idx + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-gray-900 text-sm sm:text-base truncate">{business.name}</h3>
                        <p className="text-xs sm:text-sm text-gray-600 truncate">{business.address}</p>
                        <div className="flex flex-wrap items-center gap-2 mt-1">
                          <span className="text-xs bg-gray-200 text-gray-700 px-2 py-0.5 rounded-full">
                            {business.business_type}
                          </span>
                          {business.rating && (
                            <span className="text-xs flex items-center gap-1 text-yellow-600">
                              <Star className="w-3 h-3 fill-yellow-500" />
                              {business.rating}
                            </span>
                          )}
                        </div>
                        {/* Mobile action buttons */}
                        <div className="flex gap-2 mt-3">
                          <Button
                            size="sm"
                            onClick={() => {
                              const url = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(business.address)}&travelmode=driving`;
                              window.open(url, '_blank');
                            }}
                            className="flex-1 bg-blue-600 hover:bg-blue-700 text-white text-xs py-2"
                          >
                            <Navigation className="w-3 h-3 mr-1" />
                            Navigieren
                          </Button>
                          {!isVisited && (
                            <Button
                              size="sm"
                              onClick={() => openStatusModal(business)}
                              className="flex-1 bg-brand-green hover:bg-green-600 text-white text-xs py-2"
                              data-testid={`visit-btn-${business.place_id}`}
                            >
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Besucht
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Upcoming Tours */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Geplante Touren</h2>
          
          {tours.filter(t => t.status === 'geplant').length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Calendar className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>Keine Touren geplant</p>
              <p className="text-sm">Neue Touren werden vom Admin erstellt</p>
            </div>
          ) : (
            <div className="space-y-3">
              {tours.filter(t => t.status === 'geplant').map(tour => (
                <div 
                  key={tour.id}
                  className="p-4 rounded-xl border border-gray-200 hover:border-brand-green transition-all cursor-pointer"
                  onClick={() => setActiveTour(tour)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                        <Calendar className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <div className="font-semibold text-gray-900">
                          {new Date(tour.date).toLocaleDateString('de-DE', { 
                            weekday: 'long', 
                            day: 'numeric', 
                            month: 'long' 
                          })}
                        </div>
                        <div className="text-sm text-gray-600 flex items-center gap-2">
                          <MapPin className="w-4 h-4" />
                          {tour.location}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-bold text-gray-900">{tour.business_count}</div>
                      <div className="text-xs text-gray-500">Betriebe</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Business Status Modal */}
      <BusinessStatusModal
        isOpen={showStatusModal}
        onClose={() => {
          setShowStatusModal(false);
          setSelectedBusiness(null);
        }}
        business={selectedBusiness}
        token={token}
        onStatusUpdated={handleStatusUpdated}
      />
    </div>
  );
};

export default VertriebDashboardPage;
